import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';

import { ContenderScore } from '#src/components/lineup/Contender/ContenderFooter/ContenderScore/index.tsx';
import { CardLayout } from '#src/components/so5/LastFixtureStory/CardLayout/index.tsx';
import { getLineupDisplayName } from '#src/lib/lineup/getLineupDisplayName.ts';

import { StreakProgressAnimated } from './StreakProgressAnimated/index.tsx';
import { StreakRewardAnimated } from './StreakRewardAnimated/index.tsx';
import type { StreakCard_thresholdsStreakTask } from './__generated__/index.graphql.ts';

const LeagueHeader = styled.img`
  width: 100%;
  max-height: calc(5 * var(--unit));
`;
const Wrapper = styled(Vertical)`
  width: 100%;
  padding: var(--unit) var(--triple-unit);
`;

type Props = {
  completedStreakTask: StreakCard_thresholdsStreakTask;
  focused?: boolean;
  delay?: number;
};
export const StreakCard = ({
  completedStreakTask,
  focused,
  delay = 0,
}: Props) => {
  const { completedBy, currentThreshold } = completedStreakTask;

  if (!completedBy) {
    return null;
  }

  const { managerTeam, so5Leaderboard } = completedBy;
  const { so5League } = so5Leaderboard || {};
  const rewardConfig = currentThreshold.rewardConfigs.at(0);

  return (
    <CardLayout
      so5Lineup={completedBy}
      bgRgbColor="255, 78, 34"
      borderColor="linear-gradient(323deg, #FEF314 -3.1%, #FF4E22 52.7%)"
      delay={delay}
      topLeftIcon={null}
      icon={
        !!rewardConfig && (
          <StreakRewardAnimated
            delay={delay + 300}
            anyRewardConfig={rewardConfig}
          />
        )
      }
      title={getLineupDisplayName({
        so5Leaderboard,
        so5Lineup: completedBy,
        managerTeam,
        sport: completedBy.so5Fixture.sport,
      })}
      footer={<LeagueHeader src={so5League?.headerUrl} alt="" />}
      withFire
    >
      <Wrapper gap={2} center>
        <span style={{ color: 'var(--c-rewards-orange)' }}>
          <ContenderScore
            Typography={BrandS}
            exponentColor="currentColor"
            score={currentThreshold.score}
          />
        </span>
        <StreakProgressAnimated
          completedStreakTask={completedStreakTask}
          animate={focused}
          delay={delay + 1000}
        />
      </Wrapper>
    </CardLayout>
  );
};

StreakCard.fragments = {
  thresholdsStreakTask: gql`
    fragment StreakCard_thresholdsStreakTask on ThresholdsStreakTask {
      id
      currentThreshold {
        score
        rewardConfigs {
          id
          ...AnyRewardConfig_anyRewardConfigInterface
        }
      }
      completedBy {
        id
        so5Fixture {
          slug
          sport
        }
        managerTeam {
          id
          ...getLineupDisplayName_managerTeam
        }
        so5Leaderboard {
          slug
          seasonality
          seasonalityName
          so5League {
            slug
            headerUrl
          }
          ...getLineupDisplayName_so5Leaderboard
        }
        ...CardLayout_so5lineup
        ...getLineupDisplayName_so5Lineup
      }
      ...StreakProgressAnimated_thresholdsStreakTask
    }
    ${CardLayout.fragments.so5Lineup}
    ${StreakProgressAnimated.fragments.thresholdsStreakTask}
    ${StreakRewardAnimated.fragments.anyRewardConfigInterface}
    ${getLineupDisplayName.fragments.so5Lineup}
    ${getLineupDisplayName.fragments.managerTeam}
    ${getLineupDisplayName.fragments.so5Leaderboard}
  ` as TypedDocumentNode<StreakCard_thresholdsStreakTask>,
};
